/**
 * The two facts about the offer that are also read by something other than a
 * person: the prices, and the answers to the questions buyers ask before they
 * buy.
 *
 * They live here because the build writes them twice — once as the screen and
 * once as JSON-LD in `index.html`, for the search engines and the assistants
 * that read a page without rendering it. Two copies of a price is how a page
 * ends up advertising a number the checkout does not charge, so there is one
 * copy and both readers take it from here.
 */

export interface SalesOffer {
  name: string;
  /** In reais, as a number, because the JSON-LD `price` field is not a string. */
  price: number;
  /** What the buyer walks away with. Used by the structured data, not by the screen. */
  description: string;
  /**
   * Our own identifier, not Hotmart's. The `ucode` would work and is the wrong
   * thing to publish: it is the key the billing adapter maps on, and a public
   * page is a poor place to teach anyone what it is.
   */
  sku: string;
}

/**
 * Three steps, cheapest first. The higher step includes the lower, so the
 * numbers below are cumulative rather than exclusive: the pack's 200 counts
 * the starter's 20.
 *
 * **Every count here is a claim about a file.** 20 and 200 are what
 * `COMMITTED_STARTER_COUNT` and `COMMITTED_TOTAL_COUNT` in
 * `roteirovisk/service/tests/catalogue.test.ts` pin, and those are what the
 * catalogue actually serves. Advertising a count it cannot serve is not
 * optimism, it is publicidade enganosa (CDC art. 37).
 *
 * The starter and the pack still have live Hotmart products and still open
 * their entitlements — an old e-mail's link has to keep working. What changed
 * on 2026-09-01 is that the public page stops offering them: see
 * `advertisedOffers`.
 */
export const OFFERS: Record<'starter' | 'pack' | 'generator', SalesOffer> = {
  starter: {
    name: '20 Prompts',
    price: 37,
    description:
      'Vinte prompts escritos para quem ainda não tem público nem arquivo, do primeiro Reels à primeira vez que você diz não.',
    sku: 'roteirovisk-pacote-starter'
  },
  pack: {
    name: '200 Prompts Customizáveis',
    price: 97,
    description: 'Duzentos prompts prontos e organizados por objetivo.',
    sku: 'roteirovisk-pacote-prompts'
  },
  generator: {
    // Hotmart product 8249404. Its base price moved from R$ 297,00 to R$ 37,00
    // on 2026-09-01, which is why this number is the cheapest on the page while
    // the product is the most complete one.
    name: 'Gerador de Roteiros Inteligentes',
    price: 37,
    description:
      'A biblioteca inteira, e o gerador que escreve o roteiro e não só o prompt, com cem créditos que não expiram.',
    sku: 'roteirovisk-gerador-prompts'
  }
};

export interface BundleItem {
  name: string;
  /**
   * What the item would cost bought alone, in reais, or `null` when nothing is
   * charged for it.
   *
   * **Only the first line is a price anyone has ever paid**: it reads
   * `OFFERS.pack.price`, and that checkout is still live, so a bigger number
   * struck through here would be contradicted by a link the same company sells.
   * The other three are valuations of things never sold separately.
   */
  price: number | null;
  detail: string;
}

/**
 * The offer, item by item, and the sum a buyer is being compared against.
 *
 * One array so the total, the discount and the saving are arithmetic instead of
 * three numbers typed in three places — the arrangement that ends with a page
 * claiming a percentage its own prices do not produce.
 */
export const BUNDLE: BundleItem[] = [
  {
    name: '200 prompts customizáveis',
    price: OFFERS.pack.price,
    detail:
      'Reels, Carrossel e Stories. Você troca o que está entre colchetes e cola na IA que já usa.'
  },
  {
    // Not "roteiros prontos": they are written on demand by the generator, one
    // credit each. A page promising a hundred finished scripts would be
    // promising a file that does not exist.
    name: '100 roteiros no gerador',
    price: 147,
    detail:
      'O gerador escreve cada um com o seu nicho, a sua voz e a sua oferta. Os créditos não expiram.'
  },
  {
    name: 'Análise do seu perfil',
    price: 97,
    // Not "nos últimos 90 dias", which is what the mockup wrote and what the
    // reading refuses to say: the public endpoint answers with the most recent
    // posts, and how far back those reach depends on how often she posts. The
    // reading prints the real span (`windowSentence`), so the card cannot
    // promise a fixed window in front of it.
    detail: 'O seu post com mais engajamento entre os mais recentes, e o motivo dele ter ido bem.'
  },
  {
    name: 'O roteiro do próximo Reel',
    price: 67,
    detail:
      'Cenas com gancho, tempo de cada take e a legenda, tiradas do post que já funcionou.'
  },
  {
    name: 'Prompt novo que entrar na biblioteca',
    price: null,
    detail: 'Prompt novo entra na sua conta sem cobrança nova, para sempre.'
  }
];

/** What the items add up to, which is the only number the page anchors against. */
export function bundleTotal(): number {
  return BUNDLE.reduce((total, item) => total + (item.price ?? 0), 0);
}

/** How much she does not spend, as a number and not as a second opinion. */
export function bundleSavings(): number {
  return bundleTotal() - OFFERS.generator.price;
}

/** Rounded, because a percentage with decimals reads as a calculation. */
export function bundleDiscountPercent(): number {
  return Math.round((bundleSavings() / bundleTotal()) * 100);
}

/**
 * How much each of the 300 things in the account costs at R$ 37,00: the 200
 * prompts the catalogue serves plus the 100 scripts the generator writes.
 */
export const BUNDLE_PIECES = 300;

/**
 * How full the discounted lot is, as a percentage.
 *
 * **Nothing measures this.** There is no lot counter in the service and no
 * sale decrements it; the number is a constant, chosen, and the bar drawn from
 * it says "quase esgotado" on a page that cannot know. Escassez sem lastro is
 * the same CDC art. 37 problem as a count the catalogue cannot serve, and the
 * repository's own prompt catalogue refuses to write "últimas vagas" without
 * it. Kept at the owner's explicit instruction on 2026-09-01; delete this
 * constant and every `LOT_` reference to take the claim off the page.
 */
export const LOT_FILLED_PERCENT = 95;

/** `R$ 97`, from the one number above. */
export function brl(price: number): string {
  return `R$ ${price}`;
}

/**
 * `R$ 37,00`. Every price the page prints goes through here, so the cents are
 * on all of them or on none.
 *
 * Rounded down to the centavo rather than to the real: a page that rounds
 * R$ 148,50 up to R$ 149 is advertising a price nobody is charged.
 */
export function brlWithCents(price: number): string {
  return `R$ ${(Math.floor(price * 100) / 100).toFixed(2).replace('.', ',')}`;
}

export interface SalesQuestion {
  question: string;
  answer: string;
}

/**
 * The six objections that come up before the card comes out, answered in the
 * order they arrive. Every answer restates something the page already promises
 * elsewhere — none of them introduces a claim, which is the line the offer
 * document draws and the CDC art. 37 draws behind it.
 *
 * They are also the shape an assistant quotes: a real question as the heading,
 * and an answer that stands up on its own without the paragraph above it. Every
 * price in them is interpolated rather than typed, for the same reason the
 * card's total is.
 */
export const FAQ: SalesQuestion[] = [
  {
    question: `Tudo isso mesmo por ${brlWithCents(OFFERS.generator.price)}?`,
    answer: `Sim. Item por item, o acesso soma ${brlWithCents(
      bundleTotal()
    )}: os 200 prompts, os 100 roteiros do gerador, a análise do perfil e o roteiro do próximo Reel entram no mesmo pagamento. Prompt sozinho não vira post, então não faz sentido vender cada parte separada.`
  },
  {
    question: 'Preciso pagar o ChatGPT para usar?',
    answer:
      'Não. O prompt que o Roteirovisk monta funciona no plano gratuito do ChatGPT, do Claude ou do Gemini. Você copia o prompt aqui e cola lá.'
  },
  {
    question: 'É mensalidade?',
    answer: `Não. São ${brlWithCents(
      OFFERS.generator.price
    )} uma vez: você paga uma vez, o acesso não expira e não há renovação nem cobrança recorrente.`
  },
  {
    question: 'E se eu não gostar?',
    // Seven days is the CDC's own article 49 window. Naming the article was
    // dropped from the answer, not the right.
    answer: 'Você tem 7 dias de garantia para pedir o seu dinheiro de volta.'
  },
  {
    // The old answer said the product "não se conecta às suas redes", which
    // stopped being true when the handle started being read. The half that
    // matters to her — no password, no account access — is still true and is
    // what the question actually asks.
    //
    // The list of networks is part of the answer, not decoration: since the
    // media kit shipped (ADR-0026) this reads three of them, and naming only
    // Instagram would be a true sentence about a smaller product than the one
    // she is buying.
    question: 'Preciso dar a senha do meu Instagram?',
    answer:
      'Não. O Roteirovisk nunca pede a senha de rede nenhuma e não acessa a sua conta. Com o seu @, ele lê só o que já está público no seu perfil do Instagram, do TikTok e do YouTube, como seguidores, publicações e views, e você escolhe quais redes adicionar. O que ele devolve é texto para você usar onde quiser.'
  },
  {
    question: 'Os 200 prompts servem para o meu nicho?',
    answer:
      'Cada prompt tem campos entre colchetes: você escreve o seu nicho, o seu jeito de falar e a sua oferta. O mesmo prompt de bastidor serve para bolo de casamento e para consultoria.'
  }
];

export type Product = 'PROMPT_PACK_STARTER' | 'PROMPT_PACK' | 'PROMPT_GENERATOR';

/**
 * The canonical public hotlinks, one per step of the ladder. A `Product`
 * missing from here has no Hotmart product yet: its card draws without a link
 * rather than with one that 404s.
 */
const CHECKOUTS: Partial<Record<Product, string>> = {
  PROMPT_PACK_STARTER: 'https://pay.hotmart.com/D107121470H',
  PROMPT_PACK: 'https://pay.hotmart.com/V107146306Y',
  PROMPT_GENERATOR: 'https://pay.hotmart.com/J107014986M?checkoutMode=10'
};

const OVERRIDES: Record<Product, string | undefined> = {
  PROMPT_PACK_STARTER: import.meta.env.VITE_CHECKOUT_PROMPT_PACK_STARTER,
  PROMPT_PACK: import.meta.env.VITE_CHECKOUT_PROMPT_PACK,
  PROMPT_GENERATOR: import.meta.env.VITE_CHECKOUT_PROMPT_GENERATOR
};

/**
 * The R$ 197 upgrade needs its own Hotmart offer. Falling back to the R$ 297
 * checkout would advertise one price and charge another.
 */
export function generatorUpgradeCheckout(): string | null {
  return import.meta.env.VITE_CHECKOUT_PROMPT_GENERATOR_UPGRADE?.trim() || null;
}

/** Null when there is nowhere to send her, which is not the same as a broken link. */
export function checkout(name: Product): string | null {
  return OVERRIDES[name]?.trim() || CHECKOUTS[name] || null;
}

/** Which `OFFERS` key each product is, so one fact drives both surfaces. */
const OFFER_KEY: Record<Product, keyof typeof OFFERS> = {
  PROMPT_PACK_STARTER: 'starter',
  PROMPT_PACK: 'pack',
  PROMPT_GENERATOR: 'generator'
};

/**
 * What the public page puts in front of a visitor.
 *
 * One offer since 2026-09-01, and one for a reason the ladder cannot survive:
 * the generator's Hotmart price is now the same R$ 37,00 the starter charges
 * for twenty prompts, so a ladder drawn beside it would be asking her to
 * compare a product against a strictly worse one at the same price. The other
 * two keep their hotlinks and their entitlements — a link in an old e-mail has
 * to keep working — they are simply no longer offered here.
 */
const ADVERTISED: Product[] = ['PROMPT_GENERATOR'];

/**
 * The offers the page advertises right now, in the order it draws them.
 *
 * **Advertised is one fact, read by the screen and by the structured data.** A
 * step the page does not offer must not appear as a `Product` with a price in
 * the JSON-LD either: that is machine-readable commerce telling an assistant to
 * send someone to buy something the page does not sell. The card being absent
 * and the markup being silent are the same decision, so they read the same
 * value. A product whose checkout resolves to nothing drops out of both.
 */
export function sellableOffers(): Array<keyof typeof OFFERS> {
  return ADVERTISED.filter((name) => checkout(name) !== null).map((name) => OFFER_KEY[name]);
}
